/**
 * Client-side planning store (see types.ts for the contract).
 *
 * Holds one planning, keeps it in sync with the server through ChangeSets
 * (from POST /changes and the SSE stream) and exposes immutable snapshots to
 * React via useSyncExternalStore.
 */
import { useSyncExternalStore } from 'react';
import type {
  AssignmentDto,
  ChangeOp,
  ChangeRequest,
  ChangeSet,
  DependencyDto,
  PlanningDataDto,
  PlanningSummaryDto,
  ResourceDto,
  TaskDto,
} from '../../shared/api.js';
import { MINUTES_PER_DAY, nowLocal, type LocalMinutes } from '../../shared/time.js';
import { HttpError, api, errorMessage, qs } from '../lib/api.js';
import { getMe, hasRole, subscribeMe } from '../lib/session.js';
import { dependencyKey, type PlanningStore, type PlanningStoreState } from './types.js';

/** How long a revision gap may stay open before the store reloads everything. */
const GAP_RELOAD_MS = 2500;
/** Reopening a closed event stream: first delay, doubled up to the maximum. */
const REOPEN_MIN_MS = 2000;
const REOPEN_MAX_MS = 60_000;
/** EventSource.CLOSED (the global may be missing, e.g. in tests). */
const EVENT_SOURCE_CLOSED = 2;

/** Failures worth retrying: no response, a server error, a timeout or rate limit. */
function isTransient(err: unknown): boolean {
  return !(err instanceof HttpError) || err.status >= 500 || err.status === 408 || err.status === 429;
}

/** Default window: everything that finishes after ~3 months ago. */
export function defaultWindow(): { from: LocalMinutes | null; to: LocalMinutes | null } {
  return { from: nowLocal() - 92 * MINUTES_PER_DAY, to: null };
}

function emptyState(): PlanningStoreState {
  return {
    planningId: null,
    planning: null,
    settings: null,
    revision: 0,
    status: 'idle',
    reloading: false,
    error: null,
    window: { from: null, to: null },
    tasks: new Map(),
    taskOrder: [],
    children: new Map(),
    parent: new Map(),
    dependencies: new Map(),
    assignments: new Map(),
    assignmentsByTask: new Map(),
    assignmentsByResource: new Map(),
    resources: new Map(),
    resourceOrder: [],
    calendars: new Map(),
    editLock: null,
    readOnly: true,
    pending: 0,
    lastChange: null,
  };
}

/** Row order + outline levels -> parent/children (blank rows have no children). */
function deriveTree(tasks: Map<number, TaskDto>): Pick<PlanningStoreState, 'taskOrder' | 'children' | 'parent'> {
  const taskOrder = [...tasks.values()].sort((a, b) => a.seq - b.seq).map((t) => t.uid);
  const children = new Map<number | null, number[]>();
  const parent = new Map<number, number | null>();
  const stack: TaskDto[] = [];
  for (const uid of taskOrder) {
    const t = tasks.get(uid)!;
    while (stack.length && stack[stack.length - 1]!.outlineLevel >= t.outlineLevel) stack.pop();
    const p = stack.length ? stack[stack.length - 1]!.uid : null;
    parent.set(uid, p);
    let list = children.get(p);
    if (!list) children.set(p, (list = []));
    list.push(uid);
    if (!t.isNull) stack.push(t);
  }
  return { taskOrder, children, parent };
}

function indexAssignments(assignments: Map<number, AssignmentDto>): Pick<PlanningStoreState, 'assignmentsByTask' | 'assignmentsByResource'> {
  const byTask = new Map<number, number[]>();
  const byResource = new Map<number, number[]>();
  for (const a of assignments.values()) {
    (byTask.get(a.taskUid) ?? byTask.set(a.taskUid, []).get(a.taskUid)!).push(a.uid);
    (byResource.get(a.resourceUid) ?? byResource.set(a.resourceUid, []).get(a.resourceUid)!).push(a.uid);
  }
  return { assignmentsByTask: byTask, assignmentsByResource: byResource };
}

function resourceOrderOf(resources: Map<number, ResourceDto>): number[] {
  return [...resources.values()].sort((a, b) => a.seq - b.seq).map((r) => r.uid);
}

function computeReadOnly(editLock: PlanningStoreState['editLock']): boolean {
  return editLock !== null || !hasRole(getMe(), 'planner');
}

function randomId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

class PlanningStoreImpl implements PlanningStore {
  readonly clientId = randomId();
  private state: PlanningStoreState = emptyState();
  private listeners = new Set<() => void>();
  private events: EventSource | null = null;
  private sseHadError = false;
  /** Pending reopen of a stream the browser gave up on, and the delay before the next one. */
  private reopenTimer: ReturnType<typeof setTimeout> | null = null;
  private reopenDelay = REOPEN_MIN_MS;
  /** ChangeSets that arrived ahead of a missing revision, keyed by fromRevision. */
  private buffered = new Map<number, ChangeSet>();
  private gapTimer: ReturnType<typeof setTimeout> | null = null;
  private loadSeq = 0;
  private loadInFlight = false;
  /** ChangeSets applied while a (re)load was in flight; replayed when newer than the loaded snapshot. */
  private appliedDuringLoad: ChangeSet[] = [];

  constructor() {
    subscribeMe(() => this.set({ readOnly: computeReadOnly(this.state.editLock) }));
  }

  getState = (): PlanningStoreState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<PlanningStoreState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  async load(planningId: string, window = defaultWindow()): Promise<void> {
    const seq = ++this.loadSeq;
    if (planningId !== this.state.planningId) {
      this.closeEvents();
      this.buffered.clear();
      this.state = emptyState();
    }
    // Refreshing a planning that is already shown happens in the background:
    // views stay mounted (and keep scroll/selection) until the new data arrives.
    const silent = this.state.status === 'ready';
    this.set(silent ? { reloading: true } : { planningId, status: 'loading', error: null, window });
    this.loadInFlight = true;
    this.appliedDuringLoad = [];
    try {
      const data = await api.get<PlanningDataDto>(`/api/plannings/${planningId}/data${qs({ from: window.from, to: window.to })}`);
      if (seq !== this.loadSeq) return;
      this.loadInFlight = false;
      this.replaceWith(data);
      this.openEvents(planningId);
    } catch (err) {
      if (seq !== this.loadSeq) return;
      this.loadInFlight = false;
      this.appliedDuringLoad = [];
      // Dutch, user-facing text (a dropped connection is "Failed to fetch" otherwise).
      const error = errorMessage(err);
      this.set(silent ? { error, reloading: false } : { status: 'error', error });
      throw err;
    }
  }

  async extendWindow(from: LocalMinutes | null, to: LocalMinutes | null): Promise<void> {
    const cur = this.state.window;
    const next = {
      from: from === null || cur.from === null ? null : Math.min(from, cur.from),
      to: to === null || cur.to === null ? null : Math.max(to, cur.to),
    };
    if (!this.state.planningId) return;
    await this.load(this.state.planningId, next);
  }

  async reload(): Promise<void> {
    if (this.state.planningId) await this.load(this.state.planningId, this.state.window);
  }

  reset(): void {
    this.loadSeq++;
    this.loadInFlight = false;
    this.appliedDuringLoad = [];
    this.closeEvents();
    this.buffered.clear();
    this.state = emptyState();
    for (const l of this.listeners) l();
  }

  async change(ops: ChangeOp[]): Promise<ChangeSet> {
    const { planningId, revision } = this.state;
    if (!planningId) throw new Error('Geen planning geladen');
    const body: ChangeRequest = { baseRevision: revision, clientId: this.clientId, ops };
    this.set({ pending: this.state.pending + 1 });
    try {
      const cs = await api.post<ChangeSet>(`/api/plannings/${planningId}/changes`, body);
      this.applyChangeSet(cs);
      return cs;
    } finally {
      this.set({ pending: Math.max(0, this.state.pending - 1) });
    }
  }

  applyChangeSet(cs: ChangeSet): void {
    const s = this.state;
    if (cs.planningId !== s.planningId || cs.revision <= s.revision) return;
    if (cs.fromRevision > s.revision) {
      // A revision is missing (e.g. our POST answered before the SSE event of someone else's change).
      this.buffered.set(cs.fromRevision, cs);
      this.armGapTimer();
      return;
    }
    this.applyNow(cs);
    this.drainBuffered();
  }

  /** Applies buffered ChangeSets that continue the current revision, drops stale ones, and (dis)arms the gap timer. */
  private drainBuffered(): void {
    let next = this.buffered.get(this.state.revision);
    while (next) {
      this.buffered.delete(this.state.revision);
      this.applyNow(next);
      next = this.buffered.get(this.state.revision);
    }
    for (const [from, cs] of this.buffered) if (cs.revision <= this.state.revision) this.buffered.delete(from);
    if (this.buffered.size > 0) this.armGapTimer();
    else this.clearGapTimer();
  }

  private armGapTimer(): void {
    if (this.gapTimer) return;
    this.gapTimer = setTimeout(() => {
      this.gapTimer = null;
      this.reloadQuietly();
    }, GAP_RELOAD_MS);
  }

  private clearGapTimer(): void {
    if (this.gapTimer) clearTimeout(this.gapTimer);
    this.gapTimer = null;
  }

  private applyNow(cs: ChangeSet): void {
    if (this.loadInFlight) this.appliedDuringLoad.push(cs);
    const s = this.state;
    const patch: Partial<PlanningStoreState> = { revision: cs.revision, lastChange: cs };
    const { upserts, deletes } = cs;

    if (upserts.tasks.length || deletes.tasks.length) {
      const tasks = new Map(s.tasks);
      for (const uid of deletes.tasks) tasks.delete(uid);
      for (const t of upserts.tasks) tasks.set(t.uid, t);
      Object.assign(patch, { tasks }, deriveTree(tasks));
    }
    if (upserts.dependencies.length || deletes.dependencies.length) {
      const dependencies = new Map(s.dependencies);
      for (const d of deletes.dependencies) dependencies.delete(dependencyKey(d.predUid, d.succUid));
      for (const d of upserts.dependencies) dependencies.set(dependencyKey(d.predUid, d.succUid), d);
      patch.dependencies = dependencies;
    }
    if (upserts.assignments.length || deletes.assignments.length) {
      const assignments = new Map(s.assignments);
      for (const uid of deletes.assignments) assignments.delete(uid);
      for (const a of upserts.assignments) assignments.set(a.uid, a);
      Object.assign(patch, { assignments }, indexAssignments(assignments));
    }
    if (upserts.resources.length || deletes.resources.length) {
      const resources = new Map(s.resources);
      for (const uid of deletes.resources) resources.delete(uid);
      for (const r of upserts.resources) resources.set(r.uid, r);
      Object.assign(patch, { resources, resourceOrder: resourceOrderOf(resources) });
    }
    if (upserts.calendars.length || deletes.calendars.length) {
      const calendars = new Map(s.calendars);
      for (const uid of deletes.calendars) calendars.delete(uid);
      for (const c of upserts.calendars) calendars.set(c.uid, c);
      patch.calendars = calendars;
    }
    if (upserts.settings) patch.settings = upserts.settings;
    this.set(patch);
  }

  private replaceWith(data: PlanningDataDto): void {
    const tasks = new Map(data.tasks.map((t) => [t.uid, t] as const));
    const assignments = new Map(data.assignments.map((a) => [a.uid, a] as const));
    const resources = new Map(data.resources.map((r) => [r.uid, r] as const));
    const dependencies = new Map<string, DependencyDto>(data.dependencies.map((d) => [dependencyKey(d.predUid, d.succUid), d] as const));
    const { settings, ...planning } = data.planning;
    const editLock = planning.editLock;
    // ChangeSets newer than the snapshot (applied or buffered while it was loading) are replayed on top of it.
    const carry = [...this.appliedDuringLoad, ...this.buffered.values()].filter((cs) => cs.revision > data.revision);
    this.appliedDuringLoad = [];
    this.buffered = new Map(carry.map((cs) => [cs.fromRevision, cs] as const));
    this.set({
      planning: planning as PlanningSummaryDto,
      settings,
      revision: data.revision,
      status: 'ready',
      reloading: false,
      error: null,
      window: data.window,
      tasks,
      ...deriveTree(tasks),
      dependencies,
      assignments,
      ...indexAssignments(assignments),
      resources,
      resourceOrder: resourceOrderOf(resources),
      calendars: new Map(data.calendars.map((c) => [c.uid, c] as const)),
      editLock,
      readOnly: computeReadOnly(editLock),
      lastChange: null,
    });
    this.drainBuffered();
  }

  private reloadQuietly(): void {
    void this.reload().catch(() => undefined);
  }

  /** Catches up after the stream was down: reloads when the revision moved on, and takes over a changed edit lock. */
  private catchUp(p: PlanningSummaryDto): void {
    if (p.id !== this.state.planningId) return;
    if (p.revision !== this.state.revision) {
      this.reloadQuietly();
      return;
    }
    if (JSON.stringify(p.editLock) !== JSON.stringify(this.state.editLock)) {
      const planning = this.state.planning ? { ...this.state.planning, editLock: p.editLock } : null;
      this.set({ editLock: p.editLock, planning, readOnly: computeReadOnly(p.editLock) });
    }
  }

  /**
   * The browser gave up on the stream: it does not reconnect after an HTTP
   * error such as a 401 (session ended) or a 502 (server restarting). One
   * request tells which: a 401 reaches the session handler (which ends the
   * session; the stream stays closed), other failures reopen the stream with
   * a growing delay.
   */
  private streamClosed(es: EventSource, planningId: string): void {
    es.close();
    if (this.events === es) this.events = null;
    void api
      .get<PlanningSummaryDto>(`/api/plannings/${planningId}`)
      .then(
        (p) => {
          this.catchUp(p);
          return true;
        },
        (err: unknown) => isTransient(err),
      )
      .then((reopen) => {
        if (!reopen || this.state.planningId !== planningId || this.state.status === 'idle' || this.events || this.reopenTimer) return;
        const delay = this.reopenDelay;
        this.reopenDelay = Math.min(delay * 2, REOPEN_MAX_MS);
        this.reopenTimer = setTimeout(() => {
          this.reopenTimer = null;
          // A reload in the meantime has opened a new stream already.
          if (this.state.planningId !== planningId || this.events) return;
          // Events may have been missed meanwhile: onopen checks the revision.
          this.sseHadError = true;
          this.openEvents(planningId);
        }, delay);
      });
  }

  private openEvents(planningId: string): void {
    if (this.events || typeof EventSource === 'undefined') return;
    const es = new EventSource(`/api/plannings/${planningId}/events`, { withCredentials: true });
    es.addEventListener('changes', (e) => {
      try {
        this.applyChangeSet(JSON.parse((e as MessageEvent<string>).data) as ChangeSet);
      } catch {
        this.reloadQuietly();
      }
    });
    es.addEventListener('reload', () => this.reloadQuietly());
    es.addEventListener('lock', (e) => {
      try {
        const editLock = JSON.parse((e as MessageEvent<string>).data) as PlanningStoreState['editLock'];
        const planning = this.state.planning ? { ...this.state.planning, editLock } : null;
        this.set({ editLock, planning, readOnly: computeReadOnly(editLock) });
      } catch {
        this.reloadQuietly();
      }
    });
    es.onerror = () => {
      this.sseHadError = true;
      // CONNECTING: the browser retries by itself (network hiccup, server ended the response).
      if (es.readyState === EVENT_SOURCE_CLOSED && this.events === es) this.streamClosed(es, planningId);
    };
    es.onopen = () => {
      this.reopenDelay = REOPEN_MIN_MS;
      // After a reconnect we may have missed events: refresh when the revision moved on.
      if (!this.sseHadError) return;
      this.sseHadError = false;
      void api
        .get<PlanningSummaryDto>(`/api/plannings/${planningId}`)
        .then((p) => this.catchUp(p))
        .catch(() => undefined);
    };
    this.events = es;
  }

  private closeEvents(): void {
    this.events?.close();
    this.events = null;
    this.sseHadError = false;
    this.reopenDelay = REOPEN_MIN_MS;
    if (this.reopenTimer) clearTimeout(this.reopenTimer);
    this.reopenTimer = null;
    this.clearGapTimer();
  }
}

export const planningStore: PlanningStore = new PlanningStoreImpl();

/** Subscribe to a slice of the store. The selector must return stable references (e.g. a Map from state). */
export function usePlanningStore<T>(selector: (s: PlanningStoreState) => T): T {
  return useSyncExternalStore(
    planningStore.subscribe,
    () => selector(planningStore.getState()),
    () => selector(planningStore.getState()),
  );
}

// ------------------------------------------------------------------ small selectors/helpers

export function assignmentsOfTask(s: PlanningStoreState, taskUid: number): AssignmentDto[] {
  return (s.assignmentsByTask.get(taskUid) ?? []).map((uid) => s.assignments.get(uid)!).filter(Boolean);
}

export function resourcesOfTask(s: PlanningStoreState, taskUid: number): ResourceDto[] {
  return assignmentsOfTask(s, taskUid)
    .map((a) => s.resources.get(a.resourceUid))
    .filter((r): r is ResourceDto => !!r);
}

/** True when a resource of kind 'status' (e.g. "PLANNING NIET DEFINITIEF") is assigned to the task. */
export function isTentative(s: PlanningStoreState, taskUid: number): boolean {
  return resourcesOfTask(s, taskUid).some((r) => r.kind === 'status');
}

/** Level-1 ancestor (the project row) of a task. */
export function projectOf(s: PlanningStoreState, taskUid: number): TaskDto | null {
  let uid: number | null | undefined = taskUid;
  let last: TaskDto | null = null;
  while (uid !== null && uid !== undefined) {
    const t = s.tasks.get(uid);
    if (!t) break;
    last = t;
    uid = s.parent.get(uid);
  }
  return last && last.outlineLevel === 1 ? last : null;
}
